import { Button, Drawer, TextField } from '@boltenergy-be/design-system'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { InstalmentBreakdownModalProps, InstalmentBreakdownInputs } from './types'
import { roundToTwoDecimals } from 'utils/number.ts'
import { FieldTypes } from 'pages/app/billing/edit-instalment/types.ts'
import { LANGUAGES } from '@boltenergy-be/shared'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { formatCurrency } from 'utils/format.ts'
import { UserTypes } from 'store/auth/types.ts'
import { isActiveContract } from 'utils/contracts.ts'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import styles from './InstalmentBreakdownDrawer.module.scss'

const InstalmentBreakdownDrawer = ({ instalment, ratios, range, onNewInstalmentSubmit, ...props }: InstalmentBreakdownModalProps) => {
  // Redux
  const { userType } = useStoreSelector((store) => store.auth)
  const { gas: gasContract } = useStoreSelector(selectCurrentServiceContracts)

  // i18n
  const { t } = useTranslation(['billing', 'common', 'validation'])
  const language = useGetLanguage()

  // React Hook Form
  const { register, watch, setValue, formState, handleSubmit } = useForm<InstalmentBreakdownInputs>({
    defaultValues: {
      total: instalment.total,
      electricity: instalment.electricity,
      gas: instalment.gas
    }
  })
  const watchElectricity = watch('electricity')
  const watchGas = watch('gas')

  // Constants
  const hasEffectiveGasContract = typeof gasContract !== 'undefined' && isActiveContract(gasContract)
  const prefixOrSuffix = language === LANGUAGES.NL ? { prefix: { as: '€' } } : { suffix: { as: '€' } }

  /**
   * Handles the instalment change event (both total & fuel fields)
   *
   * @param {FieldTypes} fieldType
   * @param {number} newTotalInstalment
   * @param {number} [electricityInputValue]
   * @param {number} [gasInputValue]
   */
  const handleInstalmentChange = (
    fieldType: FieldTypes,
    newTotalInstalment: number,
    electricityInputValue?: number,
    gasInputValue?: number
  ) => {
    const totalInstalment = newTotalInstalment || 0
    let electricityInstalment = electricityInputValue || watchElectricity
    let gasInstalment = gasInputValue || watchGas

    // The field that was changed was the "totalInstalment" field
    if (fieldType === FieldTypes.TOTAL) {
      if (newTotalInstalment) {
        electricityInstalment = ratios.electricity === 0 ? 10 : roundToTwoDecimals(totalInstalment * ratios.electricity)
        gasInstalment = ratios.electricity === 0 ? totalInstalment - 10 : roundToTwoDecimals(totalInstalment * ratios.gas)
      }

      setValue('total', totalInstalment, { shouldValidate: true })
      setValue('electricity', electricityInstalment, { shouldValidate: true })
      setValue('gas', gasInstalment, { shouldValidate: true })
    }

    // The field that was changed was the "electricityInstalment" or "gasInstalment" field
    if (fieldType === FieldTypes.FUEL) {
      setValue('total', roundToTwoDecimals(newTotalInstalment || 0), { shouldValidate: true })
    }
  }

  /**
   * Handles on submit
   * @param {InstalmentBreakdownInputs} inputs
   */
  const onSubmit = (inputs: InstalmentBreakdownInputs) => {
    onNewInstalmentSubmit(inputs)
    props.onRequestClose()
  }

  return (
    <Drawer title={t(($) => $.editInstalment.breakdown.modalTitle)} {...props}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <TextField
          {...register('total', {
            required: t(($) => $.required, {
              ns: 'validation'
            }),
            min: {
              value: range.min,
              message: t(($) => $.instalment.min, {
                amount: formatCurrency(range.min, { language }),
                ns: 'validation'
              })
            },
            max:
              userType !== UserTypes.SUPER_USER
                ? {
                    value: range.max,
                    message: t(($) => $.instalment.max, {
                      amount: formatCurrency(range.max, { language }),
                      ns: 'validation'
                    })
                  }
                : undefined,
            validate: {
              ...(userType === UserTypes.CUSTOMER && {
                minElectricityInstalment: () =>
                  watchElectricity !== 0 ||
                  t(($) => $.instalment.minElectricityInstalment, {
                    ns: 'validation'
                  })
              }),
              ...(hasEffectiveGasContract &&
                userType === UserTypes.CUSTOMER &&
                typeof watchGas !== 'undefined' && {
                  minGasInstalment: () =>
                    watchGas !== 0 ||
                    t(($) => $.instalment.minGasInstalment, {
                      ns: 'validation'
                    })
                })
            }
          })}
          error={formState?.errors?.total?.message || !!formState?.errors?.total?.type}
          label={t(($) => $.editInstalment.newInstalment)}
          type="number"
          step="0.01"
          onChange={(e) => {
            setValue('total', roundToTwoDecimals(Number(e.target.value)))
            handleInstalmentChange(FieldTypes.TOTAL, Number(e.target.value))
          }}
          onWheel={(e) => e.currentTarget.blur()}
          {...prefixOrSuffix}
        />

        <TextField
          {...register('electricity', {
            min: {
              value: 1,
              message: t(($) => $.instalment.minElectricityInstalment, {
                ns: 'validation'
              })
            },
            required: t(($) => $.required, {
              ns: 'validation'
            }),
            valueAsNumber: true
          })}
          onChange={(e) => {
            const value = roundToTwoDecimals(Number(e.currentTarget.value))
            setValue('electricity', value, { shouldValidate: true })
            handleInstalmentChange(FieldTypes.FUEL, value + (watchGas || 0), value, watchGas)
          }}
          onWheel={(e) => e.currentTarget.blur()}
          error={formState?.errors?.electricity?.message || !!formState?.errors?.electricity?.type}
          label={t(($) => $.electricity, {
            ns: 'common'
          })}
          type="number"
          step="0.01"
          {...prefixOrSuffix}
        />

        <TextField
          {...register('gas', {
            min: {
              value: 1,
              message: t(($) => $.instalment.minGasInstalment, {
                ns: 'validation'
              })
            },
            required: t(($) => $.required, {
              ns: 'validation'
            }),
            valueAsNumber: true
          })}
          onChange={(e) => {
            const value = roundToTwoDecimals(Number(e.target.value))
            setValue('gas', value, { shouldValidate: true })
            handleInstalmentChange(FieldTypes.FUEL, value + (watchElectricity || 0), watchElectricity, value)
          }}
          onWheel={(e) => e.currentTarget.blur()}
          error={formState?.errors?.gas?.message || !!formState?.errors?.gas?.type}
          label={t(($) => $.gas, {
            ns: 'common'
          })}
          type="number"
          step="0.01"
          {...prefixOrSuffix}
        />
        <Button type="submit" className="mt-auto">
          {t(($) => $.save, {
            ns: 'common'
          })}
        </Button>
      </form>
    </Drawer>
  )
}

export default InstalmentBreakdownDrawer
